import { ReactNode, useEffect, useState } from 'react';

import { PlusIcon, SettingsIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useParams } from 'react-router';

import Loading from '@/components/atoms/Loading/Loading';
import { SettingsNavigation } from '@/components/organisms/Navbar/SettingsNavigation/SettingsNavigation';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
} from '@/components/organisms/PageSubHeader';
import { Sidebar } from '@/components/organisms/Sidebar/Sidebar';
import { SidebarLayout } from '@/components/organisms/Sidebar/SidebarLayout';
import NotFound from '@/components/pages/NotFound/NotFound';
import Layout from '@/components/templates/Layout';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import PermissionsWrapper from '@/components/templates/PermissionsWrapper';
import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/hooks/usePermissions';
import { useSetDefaultSidebar } from '@/hooks/useSetDefaultSidebar';
import { useSidebar } from '@/hooks/useSidebar';
import { useTabEmptyState } from '@/hooks/useTabEmptyState';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { SidebarOptions } from '@/shared/types/sidebar';
import { RootState } from '@/shared/types/state';

import {
  APIKeyCreatedData,
  ModalAPIKeyCreated,
} from './components/ModalAPIKeyCreated/ModalAPIKeyCreated';
import { APIKeys } from './pages/APIKeys/APIKeys';
import { GenerateAPIKeyPanel } from './pages/APIKeys/GenerateAPIKeyPanel';
import { ConnectLanguageModelPanel } from './pages/languageModels/ConnectLanguageModelPanel';
import { LanguageModels } from './pages/languageModels/LanguageModels';
import { Plugins } from './pages/plugins';
import { CreatePluginPanel } from './pages/plugins/CreatePluginPanel';
import { SpeechModelModal } from './pages/speechModels/SpeechModelModal';
import { SpeechModels } from './pages/speechModels/SpeechModels';
import { selectReadableDeveloperModules } from './selectors';
import { DEV_TOOLS_SIDEBAR_KEYS, ModuleType, SpeechModel } from './types';

interface DeveloperPage {
  path: string;
  title: string;
  element: ReactNode;
  moduleType?: ModuleType;
  actionLabel?: string;
  sidebarKey?: SidebarOptions;
  onAction?: () => void;
  fill?: boolean;
}

export const DevelopersSettingsRouter = () => {
  const { t } = useTranslation();
  const { '*': splat = '' } = useParams();

  useSetDefaultSidebar(null);
  const { isOpen, open: openPanel, close: closePanel, meta: sidebarMeta } = useSidebar();
  const readableModules = useSelector(selectReadableDeveloperModules);
  const { speech_strategy } = useSelector(selectFeatureFlags);
  const { listStatus } = usePermissions();
  const isPanelOpen = isOpen(['generate_api_key', 'connect_language_model', 'create_plugin']);

  const currentPath = splat.split('/')[0];
  const [isCurrentPageEmpty, setIsCurrentPageEmpty] = useTabEmptyState(currentPath);
  const [createdKeyData, setCreatedKeyData] = useState<APIKeyCreatedData | null>(null);
  const [speechModelForm, setSpeechModelForm] = useState<{ speechModel?: SpeechModel } | null>(
    null
  );
  const openSpeechModelForm = (speechModel?: SpeechModel) => setSpeechModelForm({ speechModel });

  const speechModelsPage: DeveloperPage = {
    path: 'speech-models',
    fill: true,
    title: t('developer_tools.speech_models.title'),
    element: (
      <SpeechModels onEmptyStateChange={setIsCurrentPageEmpty} onOpenForm={openSpeechModelForm} />
    ),
    moduleType: 'speech_models',
    actionLabel: t('developer_tools.speech_models.create_title'),
    onAction: openSpeechModelForm,
  };

  const pages: DeveloperPage[] = [
    {
      path: 'api-keys',
      fill: true,
      title: t('developer_tools.api_keys'),
      element: <APIKeys onEmptyStateChange={setIsCurrentPageEmpty} />,
      moduleType: 'credentials',
      actionLabel: t('common.generate'),
      sidebarKey: DEV_TOOLS_SIDEBAR_KEYS.apikeys,
    },
    {
      path: 'llms',
      fill: true,
      title: t('developer_tools.llms'),
      element: <LanguageModels onEmptyStateChange={setIsCurrentPageEmpty} />,
      moduleType: 'language_models',
      actionLabel: t('common.add'),
      sidebarKey: DEV_TOOLS_SIDEBAR_KEYS.models,
    },
    // Without the flag the path falls through to NotFound
    ...(speech_strategy ? [speechModelsPage] : []),
    {
      path: 'event-notifications',
      fill: true,
      title: t('developer_tools.plugins'),
      element: <Plugins onEmptyStateChange={setIsCurrentPageEmpty} />,
      moduleType: 'plugins',
      actionLabel: t('common.create'),
      sidebarKey: DEV_TOOLS_SIDEBAR_KEYS.plugins,
    },
  ];

  const currentPage = pages.find((page) => page.path === currentPath);
  const firstReadablePage =
    pages.find((page) => page.moduleType && readableModules.includes(page.moduleType)) ?? pages[0];
  const moduleType = currentPage?.moduleType;
  const sidebarKey = currentPage?.sidebarKey;
  const onAction = currentPage?.onAction;

  const canRead = useSelector(
    (state: RootState) => !!moduleType && getPermissions(state, moduleType, actions.READ)
  );
  const canWrite = useSelector(
    (state: RootState) => !!moduleType && getPermissions(state, moduleType, actions.WRITE)
  );

  useEffect(() => {
    closePanel();
  }, [currentPath, closePanel]);

  return (
    <Layout>
      <SidebarLayout
        autoSaveId="developers-settings-sidebar"
        mainClassName="flex flex-col"
        sidebar={
          <Sidebar
            when={['connect_language_model', 'generate_api_key', 'create_plugin']}
            title={t('developer_tools.developers')}
          >
            {isOpen('connect_language_model') && (
              <ConnectLanguageModelPanel key={sidebarMeta?.languageModelId ?? 'new'} />
            )}
            {isOpen('generate_api_key') && <GenerateAPIKeyPanel onKeyCreated={setCreatedKeyData} />}
            {isOpen('create_plugin') && <CreatePluginPanel key={sidebarMeta?.pluginId ?? 'new'} />}
          </Sidebar>
        }
      >
        <PageSubHeader
          breadcrumbs={
            <PageSubHeaderBreadcrumbs>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbPage>
                      <div className="flex h-8 w-8 items-center justify-center">
                        <SettingsIcon size={16} className="text-foreground-muted" />
                      </div>
                      <div className="mr-4 ml-2 h-4 w-px bg-border" />
                      <span className="text-sm leading-5 font-normal">
                        {currentPage?.title ?? t('developer_tools.developers')}
                      </span>
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </PageSubHeaderBreadcrumbs>
          }
          actions={
            (sidebarKey || onAction) && canRead && !isCurrentPageEmpty ? (
              <PageSubHeaderActions>
                <Button
                  size="sm"
                  onClick={() => (onAction ? onAction() : sidebarKey && openPanel(sidebarKey))}
                  disabled={!canWrite}
                >
                  <PlusIcon size={16} />
                  {currentPage?.actionLabel}
                </Button>
              </PageSubHeaderActions>
            ) : null
          }
        />
        <div className="flex min-h-0 flex-1">
          <SettingsNavigation />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <PageContentWrapper noGutters readOnly={canRead && !canWrite}>
              <SettingsContent fill={currentPage?.fill} centerInParent={isPanelOpen}>
                <Routes>
                  <Route
                    index
                    element={
                      listStatus === 'pending' ? (
                        <Loading />
                      ) : (
                        <Navigate to={firstReadablePage.path} replace />
                      )
                    }
                  />
                  {pages.map((page) => (
                    <Route
                      key={page.path}
                      path={page.path}
                      element={
                        page.moduleType ? (
                          <PermissionsWrapper module={page.moduleType}>
                            {page.element}
                          </PermissionsWrapper>
                        ) : (
                          page.element
                        )
                      }
                    />
                  ))}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </SettingsContent>
            </PageContentWrapper>
          </div>
        </div>
      </SidebarLayout>

      <ModalAPIKeyCreated data={createdKeyData} onClose={() => setCreatedKeyData(null)} />
      {speechModelForm && (
        <SpeechModelModal
          speechModel={speechModelForm.speechModel}
          onClose={() => setSpeechModelForm(null)}
        />
      )}
    </Layout>
  );
};
